import '../../render'
import JobQueue from '../../render/job-queue'
import {RENDER_FORMATS, STATUS_TYPES} from '../../render/constants'
import {getCanvas, getPDFPromise} from './common'
import Status from '../../render/status'

import type pdfjs from 'pdfjs-dist'
import type {PDFDocumentProxy, PDFPageProxy} from 'pdfjs-dist'

const RENDER_BATCH_SIZE = 5

class MultiPageView {
  private status = new Status(RENDER_FORMATS.pdf)
  private jobQueue = new JobQueue({batchSize: RENDER_BATCH_SIZE})

  // Current number of pages rendered so far
  private pagesRendered = 0
  // How many pages the document contains
  private totalPages = 0
  // How many pages to render per load
  private pagesPerLoad = 5
  // How many pages have we been asked for?
  private pagesFetched = 0

  private shell = document.querySelector<HTMLElement>('.js-render-shell')!
  private pageContainer = this.shell.querySelector<HTMLElement>('.pdf-container')!
  private pageLoader = this.shell.querySelector<HTMLElement>('#js-click-for-more')!

  private pageNav = this.shell.querySelector<HTMLElement>('#single-page-nav')!

  private pdf!: PDFDocumentProxy

  constructor(private PDFJS: typeof pdfjs) {
    // Hide this by default, until all the pages we want to load are drawn on the DOM
    this.pageLoader.style.display = 'none'
    this.pageNav.style.display = 'none'

    // Load PDFJS worker from correct path
    const workerSrc = this.pageContainer.getAttribute('data-pdf-worker-js-path')

    if (!workerSrc) {
      this.status.set(STATUS_TYPES.error, {error: 'Missing pdf worker src path'})
      return
    }
    this.PDFJS.GlobalWorkerOptions.workerSrc = workerSrc

    window.addEventListener('resize', () => {
      // Prevent resize jank on pjax navigations
      // when a user goes back and forth using browser navigation,
      // the iframe is getting resized as DOM classes are added / removed by code on dotcom
      // There seems to be a race condition where this handler is listening
      // before those classes have been updated, which triggers a resize of 1px
      if (this.pagesRendered > 0) {
        this.resize()
      }
    })
    this.pageLoader.addEventListener('click', this.getMorePages)

    this.init()
  }

  private init = async () => {
    const url = this.pageContainer.getAttribute('data-file') ?? ''

    try {
      await this.fetch(url)
      await this.loadPages(1, this.pagesPerLoad)
    } catch (error) {
      this.status.set(STATUS_TYPES.error, {error})
    }
  }

  private fetch = async (url: string) => {
    window.debug(`Attempting to load [${url}]`)
    this.status.set(STATUS_TYPES.loading)

    try {
      this.pdf = await getPDFPromise(url, this.pageContainer.getAttribute('data-bcmap-dir') ?? '')
      this.totalPages = this.pdf.numPages
      window.debug(`Got PDF with ${this.totalPages} pages`)
      if (this.totalPages < this.pagesPerLoad) {
        this.pagesPerLoad = this.totalPages
      }

      this.status.set(STATUS_TYPES.loaded)
    } catch (reason) {
      window.debug(`Invalid PDF because ${reason}`)
      this.status.set(STATUS_TYPES.invalid, {error: 'Invalid PDF'})
    }
  }

  private loadPages = async (start: number, end: number) => {
    this.pagesFetched += end - start
    window.debug(`Loading pages from ${start} to ${end}`)

    for (let pageNumber = start; pageNumber <= end; pageNumber++) {
      try {
        const page = await this.pdf.getPage(pageNumber)
        await this.renderPage(page)
      } catch (error) {
        window.debug('Error loading PDF', error)
      }
    }
  }

  private renderPage = async (page: PDFPageProxy) => {
    const [canvas, error] = await getCanvas(page)

    if (error) {
      this.status.set(STATUS_TYPES.fatal, {
        error: `Error loading PDF page number ${page.pageNumber}`,
      })
      return
    }

    this.pageContainer.append(canvas)

    this.pagesRendered += 1
    this.updateContainerSize()
  }

  private updateContainerSize = () => {
    const allPagesLoaded = this.pagesRendered === this.totalPages

    if (this.pagesRendered === this.pagesPerLoad) {
      this.jobQueue.run(this.ready)
    } else if (this.pagesRendered % this.pagesPerLoad === 0) {
      // Page rendered, queue a message telling the iframe's container on dotcom to resize
      // itself if necessary
      this.jobQueue.run(() => this.resize())
    }

    if (allPagesLoaded) {
      this.jobQueue.run(() => {
        this.hidePageLoader()
      })

      this.jobQueue.run(() => {
        this.resize()
      })
    }

    if (this.pagesRendered === this.pagesFetched && !allPagesLoaded) {
      this.jobQueue.run(this.rebindPageLoader)
    }

    if (this.pagesRendered % RENDER_BATCH_SIZE === 0 || allPagesLoaded) {
      window.debug(`${this.pagesRendered}/${this.totalPages} pages ready`)
    }
  }

  private getMorePages = () => {
    this.pageLoader.classList.add(STATUS_TYPES.loading)

    let pageMax = 0
    if (this.pagesRendered + this.pagesPerLoad > this.totalPages) {
      pageMax = this.totalPages
    } else {
      pageMax = this.pagesRendered + this.pagesPerLoad
    }

    window.debug(`Getting pages from ${this.pagesRendered + 1} to ${pageMax}`)

    this.loadPages(this.pagesRendered + 1, pageMax)
  }

  private ready = () => {
    window.debug('Sending ready')
    this.status.set(STATUS_TYPES.ready)
  }

  private resize = () => {
    window.debug(`Sending resize w/${this.entireHeight()}`)
    this.status.set(STATUS_TYPES.resize, {height: this.entireHeight()})
  }

  private entireHeight = () => {
    return document.documentElement.scrollHeight
  }

  private rebindPageLoader = () => {
    if (this.pagesRendered === this.totalPages) {
      return
    }

    this.pageLoader.style.display = 'block'
    this.pageLoader.classList.remove('loading')
    this.pageLoader.removeEventListener('click', this.getMorePages)
    this.pageLoader.addEventListener('click', this.getMorePages)
  }

  private hidePageLoader = () => {
    this.pageLoader.style.display = 'none'
    this.pageContainer.classList.add('loaded')
    this.pageLoader.removeEventListener('click', this.getMorePages)
  }
}

export default MultiPageView
